/**
 * Web area screen: the twin of `HealthAreaReport.tsx`.
 *
 *   ‹ Overview
 *   [ Recruiters | Systems | AI ]      segmented tabs, sliding pill
 *   Recruiters                          the area's name
 *   all clear line, or its suggestion rows
 *   Checks in this area ▾               every check that applies, Passed /
 *                                       Needs attention; open when all clear
 *   coverage line                       what this score is not
 *   Optional · Show examples of your work   (AI tools, when recommended)
 *   Preview N changes                   when the area has proposals
 *
 * Tabs follow the WAI-ARIA pattern: arrows (mirrored in RTL), Home and End
 * move and select; only the selected tab is in the tab order.
 */
import { useRef, type CSSProperties, type KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import type { CvHealthArea } from '@tabbio/types';
import { CV_HEALTH_AREA_ORDER } from '@/lib/cv-health-model';
import { useLocale } from '@/hooks/useLocale';
import { MaterialSymbol } from '../icons/MaterialSymbol';
import { HealthAreaIcon } from './HealthAreaIcon';
import { HealthDisclosure } from './HealthDisclosure';
import { noteAreaBack } from './health-disclosure-memory';
import { HealthSuggestionList } from './HealthSuggestionList';
import {
  getAreaAllClearKey,
  getAreaCheckRows,
  getAreaLabelKey,
  getAreaTabKey,
  getProofFinding,
  getSuggestionFindings,
  isFixableFinding,
} from './cv-health-copy';
import type { HealthAreaReportProps } from './health-props';

export type { HealthAreaReportProps };

export function HealthAreaReport({
  report,
  area,
  variant,
  onAreaChange,
  onBack,
  onOpenFinding,
  onReviewArea,
  onPreviewChange,
  onDecision,
  onAddProofLink,
  busyFindingKey,
}: HealthAreaReportProps) {
  const { t } = useTranslation('common');
  const { isRTL } = useLocale();
  const tabRefs = useRef<Partial<Record<CvHealthArea, HTMLButtonElement | null>>>({});
  const index = CV_HEALTH_AREA_ORDER.indexOf(area);
  const findings = getSuggestionFindings(report, area);
  const fixable = findings.filter(isFixableFinding);
  const checks = getAreaCheckRows(report, area);
  const proof = area === 'ai_bot' ? getProofFinding(report) : null;
  const unavailable = report.areas[area]?.status === 'unavailable';

  const select = (next: CvHealthArea) => {
    onAreaChange(next);
    tabRefs.current[next]?.focus();
  };

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const count = CV_HEALTH_AREA_ORDER.length;
    const forward = isRTL ? 'ArrowLeft' : 'ArrowRight';
    const backward = isRTL ? 'ArrowRight' : 'ArrowLeft';
    let next: number | null = null;
    if (event.key === forward) next = (index + 1) % count;
    else if (event.key === backward) next = (index + count - 1) % count;
    else if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = count - 1;
    if (next === null) return;
    event.preventDefault();
    select(CV_HEALTH_AREA_ORDER[next]);
  };

  return (
    <section className="tabbio-health tabbio-health-area-view">
      <button
        type="button"
        className="tabbio-health-back"
        onClick={() => {
          noteAreaBack();
          onBack();
        }}
      >
        <MaterialSymbol name="chevron_left" size={18} className="tabbio-rtl-flip" />
        {t('cvHealth.report.overview')}
      </button>

      <div
        className="tabbio-health-tabs"
        role="tablist"
        aria-label={t('cvHealth.report.areasLabel')}
        style={{ '--tab-index': index } as CSSProperties}
        onKeyDown={onKeyDown}
      >
        <span className="tabbio-health-tab-pill" aria-hidden="true" />
        {CV_HEALTH_AREA_ORDER.map((id) => (
          <button
            key={id}
            ref={(node) => {
              tabRefs.current[id] = node;
            }}
            id={`tabbio-health-tab-${id}`}
            type="button"
            role="tab"
            aria-selected={id === area}
            aria-controls="tabbio-health-area-panel"
            tabIndex={id === area ? 0 : -1}
            className="tabbio-health-tab"
            onClick={() => onAreaChange(id)}
          >
            <HealthAreaIcon area={id} size={17} />
            <span>{t(getAreaTabKey(id))}</span>
          </button>
        ))}
      </div>

      <div
        key={area}
        id="tabbio-health-area-panel"
        role="tabpanel"
        aria-labelledby={`tabbio-health-tab-${area}`}
        className="tabbio-health-tab-panel"
      >
        <h2 className="tabbio-health-area-title">{t(getAreaLabelKey(area))}</h2>
        {findings.length > 0 ? (
          <HealthSuggestionList
            report={report}
            subtitle="fix"
            findings={findings}
            variant={variant}
            onOpenFinding={onOpenFinding}
            onPreviewChange={onPreviewChange}
            onDecision={onDecision}
            onAddProofLink={onAddProofLink}
            busyFindingKey={busyFindingKey}
          />
        ) : (
          <p className="tabbio-health-lead">
            {t(unavailable ? 'cvHealth.heading.areaUnavailable' : getAreaAllClearKey(area))}
          </p>
        )}

        {checks.length > 0 ? (
          <HealthDisclosure label={t('cvHealth.report.checksInArea')} defaultOpen={findings.length === 0}>
            <ul className="tabbio-health-named-checks">
              {checks.map((check) => (
                <li key={check.id}>
                  <span className={`tabbio-health-named-check-mark${check.passed ? ' is-passed' : ''}`}>
                    <MaterialSymbol name={check.passed ? 'check' : 'info'} size={17} />
                  </span>
                  <span className="tabbio-health-named-check-copy">
                    {t(check.nameKey)}
                    <small>{t(check.statusKey)}</small>
                  </span>
                </li>
              ))}
            </ul>
            <p className="tabbio-health-scope">{t('cvHealth.report.coverage')}</p>
          </HealthDisclosure>
        ) : (
          <p className="tabbio-health-scope">{t('cvHealth.report.coverage')}</p>
        )}

        {proof ? (
          <div className="tabbio-health-optional">
            <span className="tabbio-health-optional-eyebrow">{t('cvHealth.report.optional')}</span>
            <strong>{t('cvHealth.report.proofTitle')}</strong>
            <p>{t('cvHealth.report.proofBody')}</p>
            {onAddProofLink ? (
              <button
                type="button"
                className="tabbio-health-btn is-text tabbio-health-optional-action"
                onClick={() => onAddProofLink(proof)}
                disabled={busyFindingKey === proof.key}
              >
                {t('cvHealth.report.proofAction')}
              </button>
            ) : null}
            <small>{t('cvHealth.report.proofNote')}</small>
          </div>
        ) : null}

        {onReviewArea && fixable.length > 0 ? (
          <button
            type="button"
            className="tabbio-health-btn is-primary is-block"
            onClick={() => onReviewArea(area)}
          >
            {t('cvHealth.report.previewChanges', { count: fixable.length })}
          </button>
        ) : null}
      </div>
    </section>
  );
}

export default HealthAreaReport;
